import WedifyApiRequest from '../utils/wedify_api_request.js';

interface HistoryMessage {
  id: string;
  message: string;
  text: string; // For compatibility with ChatMessage interface
  isUser: boolean;
  timestamp: number;
  messageType?: 'user' | 'agent';
  createdAt?: string;
}

interface ConversationHistoryResponse {
  // The API returns an array directly, not wrapped in an object
  [index: number]: {
    id: number;
    content: string;
    messageType: 'USER_MESSAGE' | 'AGENT_RESPONSE';
    createdAt: string;
    externalClientId: string;
    sessionId: string;
    sender: 'user' | 'agent';
    senderEnum: 'USER' | 'AGENT';
    userMessage: boolean;
    agentResponse: boolean;
    actionPerformed?: string | null;
    conversationTurn: number;
    status: string;
    success?: boolean | null;
  };
  length: number;
}

class ConversationHistoryService {
  private static instance: ConversationHistoryService;
  private wedifyApiRequest: WedifyApiRequest;

  constructor() {
    this.wedifyApiRequest = WedifyApiRequest.getInstance();
  }

  static getInstance(): ConversationHistoryService {
    if (!ConversationHistoryService.instance) {
      ConversationHistoryService.instance = new ConversationHistoryService();
    }
    return ConversationHistoryService.instance;
  }

  async fetchConversationHistory(limit: number = 50): Promise<HistoryMessage[]> {
    try {
      console.log('🔄 Fetching conversation history with limit:', limit);

      // Use wedify.be for all environments
      const endpoint = `https://wedify.be/api/wedding-agent/conversation-history?limit=${limit}`;

      const response = await this.wedifyApiRequest.makeRequest(endpoint, 'GET');

      if (!response || !response.ok) {
        console.error('❌ Failed to fetch conversation history:', response?.status);
        return [];
      }

      const data: ConversationHistoryResponse = await response.json();
      console.log('📥 Conversation history received:', data);
      console.log('📥 Raw response structure:', {
        messagesCount: data.length || 0,
        sampleMessage: data[0] || null,
        messageTypes: Array.from(new Set(Array.from(data).map(msg => msg.messageType)))
      });

      // Convert array response to our chat message format
      const messagesArray = Array.from(data);
      const messages: HistoryMessage[] = messagesArray.map(msg => ({
        id: msg.id.toString(),
        message: msg.content, // Backend uses 'content' not 'message'
        text: msg.content,    // Also add text for compatibility
        isUser: msg.messageType === 'USER_MESSAGE', // Backend uses 'USER_MESSAGE'/'AGENT_RESPONSE'
        timestamp: new Date(msg.createdAt).getTime(),
        messageType: msg.sender as 'user' | 'agent', // Use simplified sender field
        createdAt: msg.createdAt
      }));

      // Sort by timestamp (oldest first)
      messages.sort((a, b) => a.timestamp - b.timestamp);

      console.log('✅ Processed conversation history:', {
        totalMessages: messages.length,
        userMessages: messages.filter(m => m.isUser).length,
        agentMessages: messages.filter(m => !m.isUser).length,
        timeRange: messages.length > 0 ? {
          earliest: new Date(Math.min(...messages.map(m => m.timestamp))).toLocaleString(),
          latest: new Date(Math.max(...messages.map(m => m.timestamp))).toLocaleString()
        } : null,
        sampleMessages: messages.slice(0, 3).map(m => ({
          id: m.id,
          isUser: m.isUser,
          text: m.text.substring(0, 100) + '...',
          timestamp: new Date(m.timestamp).toLocaleString()
        }))
      });

      return messages;

    } catch (error) {
      console.error('❌ Error fetching conversation history:', error);
      return [];
    }
  }

  async saveMessageToHistory(message: string, isUser: boolean): Promise<boolean> {
    try {
      // For now, we assume the backend saves messages automatically when we send chat requests
      // If needed, we can add an explicit save endpoint here
      console.log('💾 Message will be saved by backend automatically:', { message: message.substring(0, 50), isUser });
      return true;
    } catch (error) {
      console.error('❌ Error saving message to history:', error);
      return false;
    }
  }

  async clearConversationHistory(): Promise<boolean> {
    try {
      // Implement if backend provides a clear history endpoint
      console.log('🗑️ Clear history not implemented yet');
      return false;
    } catch (error) {
      console.error('❌ Error clearing conversation history:', error);
      return false;
    }
  }
}

export default ConversationHistoryService;
export type { HistoryMessage, ConversationHistoryResponse }; 